import React, {useCallback, useContext, useEffect, useState} from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";

import "../styles/FarmNFT.css";

import Math from "./utils/Math";
import Timestamp from "./utils/Timestamp";

import {ContractsContext, Web3Context} from "../contexts/Context";
import NFT from "./NFT";
import ERC20_ABI from "../ABI/ERC20";
import ERC20TokenInfo from "./ERC20TokenInfo";

import Logger from "../components/utils/Logger";
import {useToasts} from "react-toast-notifications";
import Transaction from "./common/Transaction";

const Lock = ({lock, reloadFunc}) => {
  const [nft, setNft] = useState(null);
  const [unlockTimestamp, setUnlockTimestamp] = useState(null);
  const [tokenName, setTokenName] = useState("?");
  const [tokenSymbol, setTokenSymbol] = useState("?");
  const [tokenDecimals, setTokenDecimals] = useState("?");
  const [erc20Reward, setErc20Reward] = useState("?");
  const [txHash, setTxHash] = useState(null);

  const {addToast} = useToasts();

  const web3Context = useContext(Web3Context);
  const contractsContext = useContext(ContractsContext);

  const {web3, activeAccount} = web3Context;
  const {symbolERC20, decimalsERC20} = contractsContext;

  const showError = (content) => {
    addToast(content, {appearance: 'error'})
  };

  const showSuccess = (content) => {
    addToast(content, {appearance: 'success'})
  };

  const showInfo = useCallback((content) => {
    addToast(content, {appearance: 'info'})
  }, [addToast]);

  useEffect(() => {
    Logger.log("useEffect NFT");
    const {contractNiftyEggsFarmer} = contractsContext;

    const loadNft = async () => {
      if (contractNiftyEggsFarmer) {
        const item = await contractNiftyEggsFarmer.methods.getNftFarmInfo(lock.farmId).call();
        Logger.log("Loaded NFT farm info", item);
        setNft(item);
      }
    };

    const loadUnlockTimestamp = async () => {
      if (contractNiftyEggsFarmer) {
        const res = await contractNiftyEggsFarmer.methods.getUnlockTimestamp(lock.id).call();
        Logger.log("Loaded unlock timestamp", res);
        setUnlockTimestamp(res);
      }
    };

    loadNft();
    loadUnlockTimestamp();
  }, [lock, contractsContext]);

  useEffect(() => {
    Logger.log("useEffect loadERC20");

    const loadERC20 = async () => {
      if (nft) {
        const contract = new web3.eth.Contract(ERC20_ABI, nft.tokenERC20);

        const name = await contract.methods.name().call();
        setTokenName(name);
        const symbol = await contract.methods.symbol().call();
        setTokenSymbol(symbol);
        const decimals = await contract.methods.decimals().call();
        setTokenDecimals(decimals);
        Logger.log(`Loaded token ${nft.tokenERC20}: ${name}, ${symbol}, ${decimals}`);
      }
    };

    loadERC20();
  }, [nft, web3]);

  useEffect(() => {
    Logger.log("useEffect loadBonusInfo");

    const loadBonusInfo = async () => {
      const {contractNiftyEggsFarmer} = contractsContext;
      if (contractNiftyEggsFarmer && nft) {
        const reward = await contractNiftyEggsFarmer.methods.getERC20Reward(nft.tokenERC20, nft.lockPeriod).call();
        setErc20Reward(reward);
      }
    };

    loadBonusInfo();
  }, [nft, contractsContext, symbolERC20, decimalsERC20]);

  useEffect(() => {
    Logger.log("useEffect showTx");

    const showTx = async () => {
      if (txHash) {
        showInfo
        (
          <span>Transaction hash:
            <Transaction txHash={txHash} format={false}/>
          </span>
        );
      }
    };

    showTx();
  }, [txHash, showInfo]);

  const claimFunc = async () => {
    Logger.log("Claim func", lock);

    const {contractNiftyEggsFarmer} = contractsContext;

    try {
      const lockId = lock.id.toString();

      const result = await contractNiftyEggsFarmer.methods.claim(lockId).send({
        from: activeAccount
      }, (err, txHash) => setTxHash(txHash));

      Logger.log(result);
      if (result.status === true) {
        showSuccess("Success: you claimed your NFT!");
        reloadFunc();
      } else {
        showError("Transaction failed :(");
      }
    } catch (e) {
      Logger.error(e);
      showError("Error :(");
    }
  };

  const bonusIsHidden = (bonus) => {
    return bonus <= 0;
  };

  if (!nft) {
    return (
      <div>Loading NFT...</div>
    )
  }

  return (
    <Container className="mb-3">
      <Row className="nft-row">
        <Col className="text-center">
          <NFT id={nft.id} uri={nft.uri}/>
        </Col>
        <Col className="my-auto">
          <Container>
            <Row>
              <Col><strong>ERC20 token: </strong><ERC20TokenInfo name={tokenName} symbol={tokenSymbol}
                                                                 address={nft.tokenERC20}/></Col>
            </Row>
            <Row>
              <Col><strong>Locked tokens: </strong>{Math.formatDecimals(nft.lockAmount, tokenDecimals)}</Col>
            </Row>
            <Row className={bonusIsHidden(erc20Reward) ? 'd-none' : ''}>
              <Col><strong>Bonus reward: </strong><span
                className='text-warning'>{Math.formatDecimals(erc20Reward, decimalsERC20)} {symbolERC20}</span></Col>
            </Row>
            <Row>
              <Col><strong>Remaining: </strong>{getTimeRemaining(unlockTimestamp)}</Col>
            </Row>
            <Row>
              <Col>
                {
                  hasPassed(unlockTimestamp) ?
                    <div className="d-flex ml-auto justify-content-left align-items-center">
                      <Button className="btn btn-primary btn-scale" onClick={() => claimFunc()}>Claim!</Button>
                    </div>
                    :
                    null
                }
              </Col>
            </Row>
          </Container>
        </Col>
      </Row>
    </Container>
  )
};

const getTimeRemaining = (unlockTimestamp) => {
  if (hasPassed(unlockTimestamp)) {
    return "The waiting is over, you can claim your NFT now!";
  }
  return Timestamp.timeRemaining(unlockTimestamp);
};

const hasPassed = (timestamp) => {
  return Timestamp.hasPassed(timestamp);
};

export default Lock;